// src/components/ProtectedRoute.js
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import LoadingScreen from './LoadingScreen';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const { inProgress } = useMsal();

  // Show loading during MSAL redirect flow
  if (inProgress === InteractionStatus.Login || inProgress === InteractionStatus.Logout) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent  mx-auto mb-4"></div>
          <p>Redirecting to authentication...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  return children;
};

const LoginScreen = () => {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    try {
      setLoading(true);
      await login(); // This will redirect, so the loading state won't be cleared normally
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent transform -skew-y-12"></div>
        <div className="absolute top-1/4 left-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent transform -skew-y-12"></div>
        <div className="absolute top-1/2 left-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent transform -skew-y-12"></div>
      </div>

      <div className="max-w-md w-full bg-white  shadow-2xl p-8 mx-4 relative z-10">
      
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <img
                src="/sandvik_logotype_black_rgb300.png"
                alt="Sandvik Logo"
                className="w-28 h-28 object-contain" // Increased width and height
              />
            </div>
            
          </div>

          {/* Welcome Message */}
        <div className="text-center mb-8">
          <h2 className="text-2xl text-gray-900 mb-2">
            Welcome back
          </h2>
          <p className="text-gray-600">
            Sign in with your organizational account to access your rock tools reports .
          </p>
        </div>

        {/* SSO Login Button */}
        <button
          onClick={handleLogin}
          disabled={loading}
          className="w-full flex items-center justify-center space-x-3 bg-electric-blue text-white py-3 px-4  hover:bg-electric-blue-eb3 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors mb-4"
        >
          {loading ? (
            <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent "></div>
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span>Sign in with SSO</span>
            </>
          )}
        </button>



  
          <div className="mt-8 text-center">
            <div className="text-xs text-n5">
              © {new Date().getFullYear()} Sandvik Group. All rights reserved.
            </div>
          </div>
              </div>

              {/* Bottom branding */}
      {/* <div className="absolute bottom-4 left-4 text-white opacity-50">
        <div className="text-sm">Sandvik Analytics</div>
        <div className="text-xs">Rock Tools Data Platform</div>
      </div> */}
    </div>
  );
};

export default ProtectedRoute;
